// ═══════════════════════════════════════════════════════════
// usePolling — Auto-refresh with visibility-aware pausing
// ═══════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';

/**
 * Calls `callback` every `intervalMs`. Pauses when tab is hidden.
 * Fires immediately when tab becomes visible again.
 */
export function usePolling(callback: () => void, intervalMs = 20000) {
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    function start() {
      stop();
      timer = setInterval(() => savedCallback.current(), intervalMs);
    }

    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    function handleVisibility() {
      if (document.hidden) {
        stop();
      } else {
        savedCallback.current(); // Fire immediately on tab focus
        start();
      }
    }

    document.addEventListener('visibilitychange', handleVisibility);
    start();

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [intervalMs]);
}
